import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "./SideBar";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faRetweet,
  faComment,
  faHeart,
  faQuoteRight,
  faEye,
  faBookmark,
  faExternalLinkAlt,
  faSpinner,
  faSearch,
  faNewspaper,
  faLightbulb,
  faFilter,
  faTimes,
  faUser,
  faCheckCircle,
  faFire,
  faClock,
  faUsers,
  faCamera,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";

/* ── Classification colors ─────────────────────────────────────── */
const CLASSIFICATION_STYLES = {
  Informational: { bg: "#E3F2FD", color: "#1565C0", border: "#90CAF9" },
  Promotional: { bg: "#FFF3E0", color: "#E65100", border: "#FFCC80" },
  "Personal Opinion": { bg: "#F3E5F5", color: "#7B1FA2", border: "#CE93D8" },
  Social: { bg: "#E8F5E9", color: "#2E7D32", border: "#A5D6A7" },
  "Question/Poll": { bg: "#FFF8E1", color: "#F57F17", border: "#FFE082" },
  Political: { bg: "#FCE4EC", color: "#C62828", border: "#EF9A9A" },
  "News/Updates": { bg: "#E0F7FA", color: "#00695C", border: "#80DEEA" },
};

const getClassStyle = (classification) =>
  CLASSIFICATION_STYLES[classification] || {
    bg: "#F5F5F5",
    color: "#616161",
    border: "#E0E0E0",
  };

/* ── Search query type config ──────────────────────────────────── */
const QUERY_TYPES = [
  { value: "Top", label: "Top", icon: faFire, color: "#E65100" },
  { value: "Latest", label: "Latest", icon: faClock, color: "#1565C0" },
  { value: "People", label: "People", icon: faUsers, color: "#7B1FA2" },
  { value: "Media", label: "Media", icon: faCamera, color: "#2E7D32" },
];

const API_URL = process.env.REACT_APP_API_URL_INFLUENCER;

const InfluencerTweets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [username, setUsername] = useState(searchParams.get("username") || "");
  const [page, setPage] = useState(parseInt(searchParams.get("page"), 10) || 1);

  // ── Primary (classified) tweets state ────────────────────────────
  const [primaryTweets, setPrimaryTweets] = useState([]);
  const [primarySummary, setPrimarySummary] = useState("");
  const [primaryHasNext, setPrimaryHasNext] = useState(false);
  const [primaryTotal, setPrimaryTotal] = useState(0);
  const [primaryPage, setPrimaryPage] = useState(
    parseInt(searchParams.get("page"), 10) || 1
  );

  // ── Search (Twitter) tweets state ────────────────────────────────
  const [searchTweets, setSearchTweets] = useState([]);
  const [searchHasNext, setSearchHasNext] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchQueryType, setSearchQueryType] = useState("Top");

  // ── Shared state ─────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  // ── Search input state ───────────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  // ── Mode: "primary" or "search" ──────────────────────────────────
  const isSearchMode = activeSearchQuery.trim().length > 0;

  // ── Classification filter (only for primary mode) ────────────────
  const [activeFilter, setActiveFilter] = useState("All");
  const allClassifications = ["All", ...Object.keys(CLASSIFICATION_STYLES)];

  // ════════════════════════════════════════════════════════════════
  // FETCH: Primary classified tweets
  // ════════════════════════════════════════════════════════════════
  const fetchPrimaryTweets = useCallback(
    async (user, pg) => {
      if (!user.trim()) return;
      setLoading(true);
      setError("");
      setSearched(true);

      try {
        const res = await axios.get(
          `${API_URL}/influencer/${encodeURIComponent(
            user.trim()
          )}/tweets?page=${pg}`
        );

        if (res.data?.status === "success") {
          setPrimaryTweets(res.data.tweets || []);
          setPrimarySummary(res.data.summary || "");
          setPrimaryHasNext(res.data.has_next_page || false);
          setPrimaryTotal(res.data.total_tweets || 0);
          setUsername(user.trim());
          setPrimaryPage(pg);
          setSearchParams({ username: user.trim(), page: String(pg) });
        } else {
          setError("No tweets found.");
          setPrimaryTweets([]);
        }
      } catch (err) {
        console.error("Error fetching tweets:", err);
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to fetch tweets."
        );
        setPrimaryTweets([]);
      } finally {
        setLoading(false);
      }
    },
    [setSearchParams]
  );

  // ════════════════════════════════════════════════════════════════
  // FETCH: Twitter search tweets for this influencer
  // ════════════════════════════════════════════════════════════════
  const fetchSearchTweets = useCallback(
    async (user, query, queryType, pg) => {
      if (!user.trim() || !query.trim()) return;
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(
          `${API_URL}/influencer/${encodeURIComponent(
            user.trim()
          )}/search?query=${encodeURIComponent(
            query.trim()
          )}&query_type=${encodeURIComponent(queryType)}&page=${pg}`
        );

        if (res.data?.status === "success") {
          setSearchTweets(res.data.tweets || []);
          setSearchHasNext(res.data.has_next_page || false);
          setSearchTotal(res.data.total_tweets || 0);
          setSearchPage(pg);
        } else {
          setError("No tweets found for this search.");
          setSearchTweets([]);
        }
      } catch (err) {
        console.error("Error searching tweets:", err);
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to search tweets."
        );
        setSearchTweets([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── Auto-fetch primary on mount ──────────────────────────────────
  useEffect(() => {
    const u = searchParams.get("username");
    const p = parseInt(searchParams.get("page"), 10) || 1;
    if (u) {
      setUsername(u);
      setPrimaryPage(p);
      fetchPrimaryTweets(u, p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Search submit ────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    if (!q) return;
    setActiveSearchQuery(q);
    setSearchPage(1);
    setActiveFilter("All");
    fetchSearchTweets(username, q, searchQueryType, 1);
  };

  // ── Clear search → revert to primary ─────────────────────────────
  const clearSearch = () => {
    setSearchInput("");
    setActiveSearchQuery("");
    setSearchTweets([]);
    setSearchHasNext(false);
    setSearchTotal(0);
    setSearchPage(1);
    setError("");
    setActiveFilter("All");
    // If primary tweets are already loaded, no need to refetch
    if (primaryTweets.length === 0 && username) {
      fetchPrimaryTweets(username, primaryPage);
    }
  };

  // ── Query type change (search mode) ──────────────────────────────
  const handleQueryTypeChange = (type) => {
    setSearchQueryType(type);
    if (activeSearchQuery) {
      setSearchPage(1);
      fetchSearchTweets(username, activeSearchQuery, type, 1);
    }
  };

  // ── Pagination ───────────────────────────────────────────────────
  const goToPage = (newPage) => {
    if (isSearchMode) {
      setSearchPage(newPage);
      fetchSearchTweets(username, activeSearchQuery, searchQueryType, newPage);
    } else {
      setPrimaryPage(newPage);
      setActiveFilter("All");
      fetchPrimaryTweets(username, newPage);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Derived data based on mode ───────────────────────────────────
  const currentTweets = isSearchMode ? searchTweets : primaryTweets;
  const currentPage = isSearchMode ? searchPage : primaryPage;
  const currentHasNext = isSearchMode ? searchHasNext : primaryHasNext;
  const currentTotal = isSearchMode ? searchTotal : primaryTotal;

  // ── Classification filter (primary mode only) ────────────────────
  const filteredTweets =
    !isSearchMode && activeFilter !== "All"
      ? currentTweets.filter((t) => t.classification === activeFilter)
      : currentTweets;

  // ── helpers ──────────────────────────────────────────────────────
  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEngagementLevel = (metrics) => {
    if (!metrics) return { label: "Low", color: "#6C757D" };
    const total =
      (metrics.like_count || 0) +
      (metrics.retweet_count || 0) +
      (metrics.reply_count || 0);
    if (total >= 1000) return { label: "Viral", color: "#28A745" };
    if (total >= 100) return { label: "High", color: "#FFC107" };
    if (total >= 10) return { label: "Medium", color: "#FF8C00" };
    return { label: "Low", color: "#6C757D" };
  };

  // ── Metric Pill ──────────────────────────────────────────────────
  const MetricPill = ({ icon, value, label }) => (
    <span
      style={{ display: "flex", alignItems: "center", gap: 5 }}
      title={label}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: "0.78rem" }} />
      {formatNumber(value)}
    </span>
  );

  // ── Primary Tweet Card (with classification) ─────────────────────
  const PrimaryTweetCard = ({ tweet }) => {
    const cls = getClassStyle(tweet.classification);
    const metrics = tweet.metrics || {};

    return (
      <div
        className="card fade-in"
        style={{
          padding: 20,
          marginBottom: 16,
          borderLeft: `4px solid ${cls.border}`,
        }}
      >
        {/* Header: Classification + Date */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span
            style={{
              backgroundColor: cls.bg,
              color: cls.color,
              border: `1px solid ${cls.border}`,
              padding: "4px 12px",
              borderRadius: 16,
              fontSize: "0.78rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.3,
            }}
          >
            {tweet.classification || "Uncategorized"}
          </span>

          <span
            style={{
              fontSize: "0.82rem",
              color: "var(--text-gray)",
              fontWeight: 500,
            }}
          >
            {formatDate(tweet.tweet_created_at)}
          </span>
        </div>

        {/* Tweet Text */}
        <p
          style={{
            fontSize: "0.95rem",
            color: "var(--text-dark)",
            lineHeight: 1.65,
            whiteSpace: "pre-line",
            wordBreak: "break-word",
            margin: "0 0 14px",
          }}
        >
          {tweet.text}
        </p>

        {/* Metrics Row */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            fontSize: "0.82rem",
            color: "var(--text-gray)",
            paddingTop: 12,
            borderTop: "1px solid var(--border-color)",
            alignItems: "center",
          }}
        >
          <MetricPill icon={faEye} value={metrics.view_count} label="Views" />
          <MetricPill icon={faHeart} value={metrics.like_count} label="Likes" />
          <MetricPill
            icon={faRetweet}
            value={metrics.retweet_count}
            label="Retweets"
          />
          <MetricPill
            icon={faComment}
            value={metrics.reply_count}
            label="Replies"
          />
          <MetricPill
            icon={faQuoteRight}
            value={metrics.quote_count}
            label="Quotes"
          />
          <MetricPill
            icon={faBookmark}
            value={metrics.bookmark_count}
            label="Bookmarks"
          />

          {tweet.url && (
            <a
              href={tweet.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: "auto",
                color: "var(--primary-color)",
                fontWeight: 600,
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: "0.82rem",
              }}
            >
              View on X
              <FontAwesomeIcon
                icon={faExternalLinkAlt}
                style={{ fontSize: "0.7rem" }}
              />
            </a>
          )}
        </div>
      </div>
    );
  };

  // ── Search Tweet Card (with author info + engagement badge) ──────
  const SearchTweetCard = ({ tweet }) => {
    const metrics = tweet.metrics || {};
    const author = tweet.author || {};
    const engagement = getEngagementLevel(metrics);

    return (
      <div
        className="card fade-in"
        style={{
          padding: 0,
          marginBottom: 16,
          overflow: "hidden",
          borderLeft: `4px solid ${engagement.color}`,
        }}
      >
        {/* Author Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px 0",
          }}
        >
          {/* Profile Picture */}
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              backgroundColor: "var(--background-light)",
              border: "2px solid var(--border-color)",
            }}
          >
            {author.profile_picture ? (
              <img
                src={author.profile_picture.replace("_normal", "_bigger")}
                alt={author.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  style={{ color: "var(--text-gray)" }}
                />
              </div>
            )}
          </div>

          {/* Name + Username */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  color: "var(--text-dark)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {author.name || "Unknown"}
              </span>
              {author.is_blue_verified && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#1DA1F2", fontSize: "0.8rem" }}
                />
              )}
              <span style={{ fontSize: "0.82rem", color: "var(--text-gray)" }}>
                @{author.username || "unknown"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 2,
              }}
            >
              <span style={{ fontSize: "0.78rem", color: "var(--text-gray)" }}>
                {formatDate(tweet.tweet_created_at)}
              </span>
              <span style={{ fontSize: "0.7rem", color: "var(--text-gray)" }}>
                · {formatNumber(author.followers)} followers
              </span>
              <span
                style={{
                  backgroundColor: `${engagement.color}18`,
                  color: engagement.color,
                  border: `1px solid ${engagement.color}40`,
                  padding: "1px 8px",
                  borderRadius: 10,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                {engagement.label}
              </span>
            </div>
          </div>
        </div>

        {/* Tweet Text */}
        <div style={{ padding: "12px 20px 14px" }}>
          <p
            style={{
              fontSize: "0.95rem",
              color: "var(--text-dark)",
              lineHeight: 1.65,
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            {tweet.text}
          </p>
        </div>

        {/* Metrics Row */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            fontSize: "0.82rem",
            color: "var(--text-gray)",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-color)",
            alignItems: "center",
            backgroundColor: "var(--background-light)",
          }}
        >
          <MetricPill icon={faEye} value={metrics.view_count} label="Views" />
          <MetricPill icon={faHeart} value={metrics.like_count} label="Likes" />
          <MetricPill
            icon={faRetweet}
            value={metrics.retweet_count}
            label="Retweets"
          />
          <MetricPill
            icon={faComment}
            value={metrics.reply_count}
            label="Replies"
          />
          <MetricPill
            icon={faQuoteRight}
            value={metrics.quote_count}
            label="Quotes"
          />
          <MetricPill
            icon={faBookmark}
            value={metrics.bookmark_count}
            label="Bookmarks"
          />

          {tweet.url && (
            <a
              href={tweet.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: "auto",
                color: "var(--primary-color)",
                fontWeight: 600,
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: "0.82rem",
              }}
            >
              View on X
              <FontAwesomeIcon
                icon={faExternalLinkAlt}
                style={{ fontSize: "0.7rem" }}
              />
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="dashboard-header">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              className="btn btn-outline btn-sm"
              onClick={() =>
                navigate(
                  username
                    ? `/influencer-profiles?username=${username}`
                    : "/influencer-profiles"
                )
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                borderRadius: 8,
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Profile
            </button>
            <div>
              <h1 className="dashboard-title" style={{ marginBottom: 2 }}>
                <FontAwesomeIcon
                  icon={faNewspaper}
                  style={{ marginRight: 10, color: "var(--primary-color)" }}
                />
                Tweets Feed
                {username && (
                  <span style={{ color: "var(--text-gray)", fontWeight: 400 }}>
                    {" "}
                    — @{username}
                  </span>
                )}
              </h1>
              <p className="dashboard-subtitle" style={{ margin: 0 }}>
                {isSearchMode
                  ? "Twitter search results for this influencer."
                  : "GPT-classified tweets with engagement metrics."}
              </p>
            </div>
          </div>
        </div>

        {/* ── Search Bar ───────────────────────────────────────── */}
        <div className="card" style={{ marginBottom: 20 }}>
          <form
            onSubmit={handleSearch}
            style={{ display: "flex", gap: 12, alignItems: "center" }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-gray)",
                  fontSize: "0.9rem",
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search tweets from Twitter (e.g. Parliament, Budget, AI)..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (error) setError("");
                }}
                style={{
                  paddingLeft: 42,
                  paddingRight: isSearchMode ? 42 : 14,
                  height: 46,
                  fontSize: "0.95rem",
                  borderRadius: 10,
                }}
              />
              {/* Clear button inside input */}
              {(searchInput || isSearchMode) && (
                <button
                  type="button"
                  onClick={clearSearch}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: "var(--text-gray)",
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !searchInput.trim()}
              style={{ height: 46, minWidth: 130 }}
            >
              {loading && isSearchMode ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} style={{ marginRight: 6 }} />
                  Search
                </>
              )}
            </button>
          </form>

          {/* Search mode indicator */}
          {isSearchMode && !loading && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  backgroundColor: "rgba(206, 69, 43, 0.08)",
                  borderRadius: 20,
                  border: "1px solid rgba(206, 69, 43, 0.2)",
                }}
              >
                <FontAwesomeIcon
                  icon={faGlobe}
                  style={{ color: "var(--primary-color)", fontSize: "0.8rem" }}
                />
                <span
                  style={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "var(--primary-color)",
                  }}
                >
                  Twitter results for "{activeSearchQuery}"
                </span>
                <button
                  onClick={clearSearch}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0 2px",
                    color: "var(--primary-color)",
                    fontSize: "0.78rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Clear search and go back to classified tweets"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <span
                style={{
                  fontSize: "0.78rem",
                  color: "var(--text-gray)",
                }}
              >
                Clear search to go back to classified tweets
              </span>
            </div>
          )}
        </div>

        {/* ── Query Type Tabs (search mode only) ───────────────── */}
        {isSearchMode && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {QUERY_TYPES.map((qt) => {
                  const isActive = searchQueryType === qt.value;
                  return (
                    <button
                      key={qt.value}
                      onClick={() => handleQueryTypeChange(qt.value)}
                      style={{
                        padding: "8px 20px",
                        borderRadius: 20,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        backgroundColor: isActive
                          ? qt.color
                          : "var(--background-light)",
                        color: isActive ? "#fff" : "var(--text-gray)",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <FontAwesomeIcon icon={qt.icon} />
                      {qt.label}
                    </button>
                  );
                })}
              </div>

              {searchTotal > 0 && (
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--text-gray)",
                    fontWeight: 600,
                  }}
                >
                  {searchTotal} tweet{searchTotal !== 1 ? "s" : ""} found
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────── */}
        {error && <div className="alert alert-error">{error}</div>}

        {/* ── Loading ──────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className="spinner" />
            <p style={{ color: "var(--text-gray)", marginTop: 8 }}>
              {isSearchMode
                ? `Searching Twitter for "${activeSearchQuery}"...`
                : `Fetching tweets (Page ${currentPage})...`}
            </p>
          </div>
        )}

        {/* ── Summary Card (primary mode only) ─────────────────── */}
        {!isSearchMode && primarySummary && !loading && (
          <div
            className="card fade-in"
            style={{
              borderLeft: "4px solid var(--primary-color)",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  backgroundColor: "rgba(206,69,43,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faLightbulb}
                  style={{ color: "var(--primary-color)" }}
                />
              </div>
              <h3
                style={{
                  margin: 0,
                  fontSize: "1rem",
                  color: "var(--text-dark)",
                }}
              >
                GPT Summary — Page {primaryPage}
              </h3>
            </div>
            <p
              style={{
                color: "var(--text-light)",
                fontSize: "0.9rem",
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              {primarySummary}
            </p>
          </div>
        )}

        {/* ── Classification Filter Chips (primary mode only) ──── */}
        {!isSearchMode && primaryTweets.length > 0 && !loading && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 20,
              alignItems: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faFilter}
              style={{
                color: "var(--text-gray)",
                fontSize: "0.85rem",
                marginRight: 4,
              }}
            />
            {allClassifications.map((cls) => {
              const isActive = activeFilter === cls;
              const style =
                cls === "All"
                  ? { bg: "#F5F5F5", color: "#333", border: "#CCC" }
                  : getClassStyle(cls);

              return (
                <button
                  key={cls}
                  onClick={() => setActiveFilter(cls)}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 16,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    border: `1.5px solid ${isActive ? style.color : style.border}`,
                    backgroundColor: isActive ? style.color : style.bg,
                    color: isActive ? "#fff" : style.color,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {cls}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Tweets List ──────────────────────────────────────── */}
        {!loading &&
          filteredTweets.map((tweet) =>
            isSearchMode ? (
              <SearchTweetCard key={tweet.tweet_id} tweet={tweet} />
            ) : (
              <PrimaryTweetCard key={tweet.tweet_id} tweet={tweet} />
            )
          )}

        {/* ── No results after classification filter ───────────── */}
        {!loading &&
          !isSearchMode &&
          primaryTweets.length > 0 &&
          filteredTweets.length === 0 && (
            <div className="card" style={{ textAlign: "center", padding: 32 }}>
              <p
                style={{
                  color: "var(--text-gray)",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                No tweets match the "{activeFilter}" classification on this
                page.
              </p>
            </div>
          )}

        {/* ── Pagination ───────────────────────────────────────── */}
        {!loading && filteredTweets.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              marginBottom: 24,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "var(--text-gray)",
                fontWeight: 600,
              }}
            >
              Page {currentPage} · {currentTotal} tweet
              {currentTotal !== 1 ? "s" : ""}{" "}
              {isSearchMode ? "found" : "loaded"}
            </span>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Previous
              </button>

              <button
                className="btn btn-primary btn-sm"
                disabled={!currentHasNext}
                onClick={() => goToPage(currentPage + 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}

        {/* ── Empty / Initial State ────────────────────────────── */}
        {!loading && currentTweets.length === 0 && !error && !searched && (
          <div className="card" style={{ textAlign: "center", padding: 48 }}>
            <FontAwesomeIcon
              icon={faNewspaper}
              style={{
                fontSize: "2.5rem",
                color: "var(--border-color)",
                marginBottom: 16,
              }}
            />
            <h3 style={{ color: "var(--text-gray)", fontWeight: 600 }}>
              No tweets loaded
            </h3>
            <p style={{ color: "var(--text-gray)", fontSize: "0.9rem" }}>
              Enter a username above to fetch their classified tweets.
            </p>
          </div>
        )}

        {!loading &&
          currentTweets.length === 0 &&
          searched &&
          !error &&
          !isSearchMode && (
            <div
              className="card"
              style={{ textAlign: "center", padding: 40 }}
            >
              <p
                style={{
                  color: "var(--text-gray)",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                No tweets found for @{username} on page {currentPage}.
              </p>
            </div>
          )}

        {!loading &&
          searchTweets.length === 0 &&
          isSearchMode &&
          !error && (
            <div
              className="card"
              style={{ textAlign: "center", padding: 40 }}
            >
              <p
                style={{
                  color: "var(--text-gray)",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                No Twitter results for "{activeSearchQuery}" on page{" "}
                {searchPage}.
              </p>
            </div>
          )}
      </div>
    </>
  );
};

export default InfluencerTweets;